import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { orderService } from '../../services/order.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ArrowLeftIcon, PackageIcon, MapPinIcon, UserIcon, TruckIcon } from '../../components/icons/Icons';

const FULFILLMENT_OPTIONS = [
  { value: 'processing', label: 'Processing' },
  { value: 'packed', label: 'Packed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
];

const FULFILLMENT_STEPS = ['pending', 'processing', 'packed', 'shipped', 'out_for_delivery', 'delivered'];

const StatusBadge = ({ status, size = 'sm' }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span style={{
      display: 'inline-block',
      padding: size === 'lg' ? '6px 16px' : '4px 12px',
      borderRadius: theme.borderRadius.full,
      fontSize: size === 'lg' ? theme.typography.fontSize.sm : theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.semibold,
      color: '#fff', backgroundColor: color,
    }}>{label}</span>
  );
};

const ProgressTracker = ({ currentStatus }) => {
  const currentIdx = FULFILLMENT_STEPS.indexOf(currentStatus);
  const isCancelled = currentStatus === 'cancelled';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', padding: `${theme.spacing.md} 0` }}>
      {FULFILLMENT_STEPS.map((step, idx) => {
        const isCompleted = !isCancelled && idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={step}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                backgroundColor: isCompleted ? theme.colors.success : theme.colors.gray[200],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: '12px', fontWeight: 'bold',
                border: isCurrent ? `2px solid ${theme.colors.primary.main}` : 'none',
              }}>
                {isCompleted ? '✓' : idx + 1}
              </div>
              <span style={{
                fontSize: '10px', marginTop: 4,
                color: isCompleted ? theme.colors.text.primary : theme.colors.text.disabled,
                textTransform: 'capitalize', textAlign: 'center',
                fontWeight: isCurrent ? theme.typography.fontWeight.semibold : 'normal',
              }}>
                {step.replace(/_/g, ' ')}
              </span>
            </div>
            {idx < FULFILLMENT_STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, minWidth: 20, marginBottom: 20,
                backgroundColor: !isCancelled && idx < currentIdx ? theme.colors.success : theme.colors.gray[200],
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const SellerOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await orderService.getOrderById(id);
      setOrder(response.data?.data?.order || response.data?.order || null);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderItemId, newStatus) => {
    setUpdatingId(orderItemId);
    try {
      await orderService.updateFulfillmentStatus(orderItemId, newStatus);
      toast.success('Fulfillment status updated');
      await fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <MainLayout><LoadingSpinner fullScreen /></MainLayout>;
  if (!order) return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, textAlign: 'center' }}>
        <h2>Order not found</h2>
        <Button variant="primary" onClick={() => navigate('/seller/orders')} style={{ marginTop: theme.spacing.lg }}>
          Back to Orders
        </Button>
      </div>
    </MainLayout>
  );

  const shippingAddress = order.shipping_address || {};

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.xl, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/seller/orders')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: theme.spacing.xs }}>
            <ArrowLeftIcon size={24} color={theme.colors.text.secondary} />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: isMobile ? theme.typography.fontSize.xl : theme.typography.fontSize['2xl'], fontWeight: 'bold', margin: 0 }}>
              Order #{order.order_number}
            </h1>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              {formatDate(order.createdAt || order.created_at, 'long')}
            </span>
          </div>
          <StatusBadge status={order.status} size="lg" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: theme.spacing.xl }}>
          {/* Left Column - Items */}
          <div>
            <Card padding="lg" style={{ marginBottom: theme.spacing.xl }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.lg }}>
                <PackageIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Your Items in This Order
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                {order.items?.map(item => (
                  <div key={item.id} style={{
                    padding: theme.spacing.lg,
                    borderRadius: theme.borderRadius.lg,
                    backgroundColor: theme.colors.gray[50],
                    border: `1px solid ${theme.colors.gray[200]}`,
                  }}>
                    <div style={{ display: 'flex', gap: theme.spacing.md, marginBottom: theme.spacing.md }}>
                      {item.product_image ? (
                        <img src={item.product_image} alt={item.product_name}
                          style={{ width: 80, height: 80, borderRadius: theme.borderRadius.md, objectFit: 'cover' }} />
                      ) : (
                        <div style={{
                          width: 80, height: 80, borderRadius: theme.borderRadius.md,
                          backgroundColor: theme.colors.gray[200], display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}><PackageIcon size={28} color={theme.colors.gray[400]} /></div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.base, marginBottom: 4 }}>
                          {item.product_name}
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                          Qty: {item.quantity} × {formatCurrency(item.price)}
                        </div>
                        <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.lg, marginTop: 4 }}>
                          {formatCurrency(item.subtotal)}
                        </div>
                      </div>
                    </div>

                    {/* Fulfillment Progress */}
                    <ProgressTracker currentStatus={item.fulfillment_status} />

                    {/* Update Status */}
                    {item.fulfillment_status !== 'delivered' && item.fulfillment_status !== 'cancelled' && (
                      <div style={{
                        display: 'flex', gap: theme.spacing.sm, alignItems: 'center',
                        marginTop: theme.spacing.sm, paddingTop: theme.spacing.sm,
                        borderTop: `1px solid ${theme.colors.gray[200]}`,
                      }}>
                        <TruckIcon size={16} color={theme.colors.primary.main} />
                        <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
                          Update Status:
                        </span>
                        <select
                          value={item.fulfillment_status}
                          onChange={(e) => handleStatusUpdate(item.id, e.target.value)}
                          disabled={updatingId === item.id}
                          style={{
                            flex: 1, padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                            borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.gray[300]}`,
                            fontSize: theme.typography.fontSize.sm, backgroundColor: theme.colors.white,
                            opacity: updatingId === item.id ? 0.5 : 1,
                          }}
                        >
                          {FULFILLMENT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div>
            {/* Customer Info */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                <UserIcon size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Customer
              </h3>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.7 }}>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                  {order.user?.first_name} {order.user?.last_name}
                </div>
                <div>{order.user?.email}</div>
              </div>
            </Card>

            {/* Shipping Address */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                <MapPinIcon size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Shipping Address
              </h3>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.7 }}>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                  {shippingAddress.first_name} {shippingAddress.last_name}
                </div>
                <div>{shippingAddress.address_line1}</div>
                {shippingAddress.address_line2 && <div>{shippingAddress.address_line2}</div>}
                <div>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}</div>
                {shippingAddress.phone_number && <div>Phone: {shippingAddress.phone_number}</div>}
              </div>
            </Card>

            {/* Your Earnings */}
            <Card padding="lg">
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Your Earnings
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                {order.items?.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span style={{ color: theme.colors.text.secondary }}>{item.product_name}</span>
                    <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{formatCurrency(item.seller_amount)}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderTop: `1px solid ${theme.colors.gray[200]}`, paddingTop: theme.spacing.sm,
                  fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.lg,
                }}>
                  <span>Total Earnings</span>
                  <span style={{ color: theme.colors.success }}>
                    {formatCurrency(order.items?.reduce((sum, i) => sum + parseFloat(i.seller_amount || 0), 0) || 0)}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SellerOrderDetails;
